"use client";
import React from "react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import Container from "@/components/ui/container";
import { DisplayProduct } from "@/types";
import { brandNameToSlug } from "@/lib/utils";

import ProductCard from "@/components/products/product-card";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, ShoppingCart } from "lucide-react";

interface ProductCategoriesProps {
  featuredProducts: DisplayProduct[];
}

function ScrollableProductSection({
  products,
  brandName,
  bgClass = "",
}: {
  products: DisplayProduct[];
  brandName: string;
  bgClass?: string;
}) {
  // Removed artificial loading state for better performance

  return (
    <div className={cn("relative -mx-[100vw] px-[100vw] py-8", bgClass)}>
      <div className='space-y-6'>
        <div className='relative group px-10'>
          {/* Scroll controls for larger screens */}
          <div className='absolute left-0 top-1/2 -translate-y-1/2 z-10 hidden md:block'>
            <Button
              size='icon'
              variant='outline'
              className='rounded-full shadow-md opacity-80 hover:opacity-100'
              aria-label={`Scroll ${brandName} products left`}
              onClick={() => {
                const container = document.getElementById(
                  `scroll-container-${brandName.toLowerCase().replace(/\s+/g, "-")}`
                );
                if (container) {
                  container.scrollBy({ left: -300, behavior: "smooth" });
                }
              }}
            >
              <ChevronLeft className='h-5 w-5' />
            </Button>
          </div>

          <div className='absolute right-0 top-1/2 -translate-y-1/2 z-10 hidden md:block'>
            <Button
              size='icon'
              variant='outline'
              className='rounded-full shadow-md opacity-80 hover:opacity-100'
              aria-label={`Scroll ${brandName} products right`}
              onClick={() => {
                const container = document.getElementById(
                  `scroll-container-${brandName.toLowerCase().replace(/\s+/g, "-")}`
                );
                if (container) {
                  container.scrollBy({ left: 300, behavior: "smooth" });
                }
              }}
            >
              <ChevronRight className='h-5 w-5' />
            </Button>
          </div>

          {/* Scrollable container */}
          <div
            id={`scroll-container-${brandName.toLowerCase().replace(/\s+/g, "-")}`}
            className='flex overflow-x-auto space-x-4 pb-4 scrollbar-hide snap-x snap-mandatory'
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {products.map((product, index) => (
              <div
                key={product.id || `${brandName}-${index}`}
                className='flex-shrink-0 w-[calc(50%-8px)] sm:w-[calc(50%-8px)] md:w-[calc(33.333%-10.67px)] lg:w-[calc(20%-9.6px)] snap-start'
              >
                <ProductCard
                  product={product}
                  brandName={product.brand || brandName}
                  index={index}
                  collapsed={true}
                />
              </div>
            ))}
          </div>
        </div>

        {products.length > 5 && (
          <div className='flex justify-center mt-2'>
            <Link href={`/${brandNameToSlug(brandName)}/products`}>
              <Button variant='outline' className='rounded-full'>
                View All Products
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function PromotionalBanner() {
  return (
    <div className='relative w-screen lg:w-full -translate-x-4 lg:translate-x-0 lg:px-10 mt-4 lg:mt-8'>
      {/* Main banner image */}
      <div className='relative aspect-[16/7] w-full lg:border lg:rounded-xl overflow-hidden'>
        <Image
          src='https://res.cloudinary.com/djowccubk/image/upload/v1747322017/promotions/Rock_Board_RBMT_Promotion.webp'
          alt='Rock Board RBMT Promotion'
          fill
          sizes='(max-width: 640px) 100vw, (max-width: 1024px) 95vw, 90vw'
          className='object-cover object-right-top'
          loading='lazy'
          quality={70}
        />

        {/* Overlay content */}
        <div className='absolute top-0 left-0 px-6 md:p-10 max-w-[500px] flex flex-col items-start gap-0 lg:gap-4 pb-4 h-full justify-center'>
          {/* Logo */}
          <div className='w-64 md:w-96 relative aspect-[4/1]'>
            <Image
              src='https://res.cloudinary.com/djowccubk/image/upload/v1736489830/logos/fltnbmmj6qhzsjcmloye.png'
              alt='Rock Board Logo'
              fill
              sizes='(max-width: 640px) 256px, (max-width: 768px) 384px, 400px'
              className='object-contain'
              loading='lazy'
              quality={80}
            />
          </div>

          {/* Text */}
          <p className='text-muted-foreground w-1/2 md:w-full text-sm md:text-base font-medium drop-shadow-md'>
            360 degree identification with backlit Rock Board technology.
          </p>

          {/* Button */}
          <Link href='/rock-board/products/rbmt'>
            <Button className='mt-2'>
              <ShoppingCart className='mr-2 h-4 w-4' />
              RBMT
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function ProductCategories({ featuredProducts }: ProductCategoriesProps) {
  // Group featured products by brand
  const rockBoardFeatured = React.useMemo(() => {
    return featuredProducts.filter((product) => {
      const productBrand = product.brand?.toLowerCase() || "";
      return (
        productBrand.includes("rock board") ||
        productBrand.includes("rockboard")
      );
    });
  }, [featuredProducts]);

  const xcelr8Featured = React.useMemo(() => {
    return featuredProducts.filter((product) => {
      const productBrand = product.brand?.toLowerCase() || "";
      return (
        productBrand.includes("xcelr8") || productBrand.includes("xcel r8")
      );
    });
  }, [featuredProducts]);

  return (
    <Container className='py-12'>
      <div className='flex flex-col items-center justify-center'>
        <span className='text-primary text-sm font-medium'>
          Our Best Sellers
        </span>
        <h2 className='text-4xl font-bold text-center text-foreground'>
          Featured Products
        </h2>
      </div>

      <PromotionalBanner />

      {rockBoardFeatured.length > 0 && (
        <ScrollableProductSection
          products={rockBoardFeatured}
          brandName='Rock Board'
          bgClass='bg-background'
        />
      )}

      {xcelr8Featured.length > 0 && (
        <ScrollableProductSection
          products={xcelr8Featured}
          brandName='XCELR8'
        />
      )}
    </Container>
  );
}

export default ProductCategories;
